'use client'

import { useEffect, useCallback } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useTimedCart } from '@/context/TimedCartContext'
import { formatTimestamp, formatDate } from '@/lib/timed-ui/formatting'
import { useFormatPrice } from '@/lib/timed-ui/use-format-price'
import { trackViewCart } from '@/lib/timed-ui/analytics'

const formatCountdown = (ms: number): string => {
  const totalSeconds = Math.ceil(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

const CartDrawer = () => {
  const router = useRouter()
  const pathname = usePathname()
  const { isOpen, closeCart, cart, itemCount, updateQuantity, removeItem, timeRemaining } = useTimedCart()
  const formatPrice = useFormatPrice()

  // Count distinct "removal units" — table groups count as 1, each individual item counts as 1
  const getRemovalUnitCount = useCallback(() => {
    const tableGroupKeys = new Set<string>()
    let individualCount = 0
    for (const item of cart.items) {
      if (item.seatId && item.seatData?.sectionType === 'TABLE' && item.seatData.sectionKey) {
        tableGroupKeys.add(`${item.eventId}::${item.seatData.sectionKey}`)
      } else {
        individualCount++
      }
    }
    return tableGroupKeys.size + individualCount
  }, [cart.items])

  // Get the event slug for the first item (for redirect after empty)
  const getFirstEventSlug = useCallback(() => {
    const first = cart.items[0]
    if (!first) return null
    // eventId is available; slug may be embedded in the item or derivable
    return first.eventId
  }, [cart.items])

  const handleRemoveItem = useCallback((itemId: number) => {
    const isLastUnit = getRemovalUnitCount() === 1
    if (isLastUnit) {
      if (!confirm('Remove the last item? You will be redirected back to browse events.')) return
      const eventId = getFirstEventSlug()
      removeItem(itemId)
      closeCart()
      // Redirect to event page or home
      if (eventId) {
        router.push('/')
      } else {
        router.push('/')
      }
      return
    }
    removeItem(itemId)
  }, [getRemovalUnitCount, getFirstEventSlug, removeItem, closeCart, router])

  const handleRemoveTableGroup = useCallback((seats: typeof cart.items) => {
    const isLastUnit = getRemovalUnitCount() === 1
    if (isLastUnit) {
      if (!confirm('Remove the last item? You will be redirected back to browse events.')) return
      seats.forEach(s => removeItem(s.id))
      closeCart()
      router.push('/')
      return
    }
    seats.forEach(s => removeItem(s.id))
  }, [getRemovalUnitCount, removeItem, closeCart, router])

  const handleUpdateQuantity = useCallback((itemId: number, newQty: number) => {
    if (newQty <= 0) {
      handleRemoveItem(itemId)
      return
    }
    updateQuantity(itemId, newQty)
  }, [handleRemoveItem, updateQuantity])

  // Never show cart drawer on checkout pages
  const isCheckoutPage = pathname?.startsWith('/checkout')

  // Force close if navigated to checkout while drawer was open
  useEffect(() => {
    if (isCheckoutPage && isOpen) closeCart()
  }, [isCheckoutPage, isOpen, closeCart])

  useEffect(() => {
    if (isOpen && cart.items.length > 0) {
      trackViewCart(
        cart.items.map(i => ({ ticketTypeId: i.ticketTypeId, ticketTypeName: i.ticketTypeName, quantity: i.quantity, price: i.price })),
        cart.items.reduce((sum, i) => sum + i.price * i.quantity, 0),
      )
    }
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCheckout = () => {
    closeCart()
    router.push('/checkout')
  }

  const handleContinueShopping = () => {
    closeCart()
    router.push('/')
  }

  // Always recompute from cart.items here. cart.totals can be inflated by
  // checkout-only addon state (addonSubtotal passed to describe) which
  // the drawer can't display as line items, leading to a "subtotal $140
  // but only $90 of items shown" trust break. Drawer shows just tickets.
  const itemsSubtotal = cart.items.reduce((sum, item) => sum + (item.allInPrice || item.price) * item.quantity, 0)
  const subtotal = itemsSubtotal
  const fees = 0
  const tax = 0
  const discount = 0
  const total = itemsSubtotal
  const feeBreakdown: Array<{ name: string; amount: number }> | undefined = undefined

  // Don't render on checkout to prevent flash during navigation
  if (isCheckoutPage) return null

  return (
    <div
      data-cart-drawer=""
      style={{
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: isOpen ? 'auto' : 'none',
        zIndex: isOpen ? 40 : -1,
      }}
    >
      {isOpen && (
        <div
          className="absolute inset-0 bg-black/40 transition-opacity"
          onClick={closeCart}
        />
      )}

      <div
        className={`absolute top-0 right-0 h-full bg-surface shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } w-full sm:w-[400px] flex flex-col`}
        style={{ pointerEvents: 'auto' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-surface-border">
          <h2 className="text-lg font-heading font-bold text-text-primary">
            Cart {itemCount > 0 && `(${itemCount})`}
          </h2>
          {timeRemaining !== null && timeRemaining > 0 && (
            <div className={`flex items-center gap-1.5 text-xs font-medium ${
              timeRemaining < 60000 ? 'text-error' :
              timeRemaining < 120000 ? 'text-warning' :
              'text-text-tertiary'
            }`}>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{formatCountdown(timeRemaining)}</span>
            </div>
          )}
          <button
            onClick={closeCart}
            className="p-2 text-text-tertiary hover:text-text-primary rounded-lg hover:bg-surface-tertiary transition-colors"
            aria-label="Close cart"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Cart items */}
        <div className="flex-1 overflow-y-auto p-4">
          {cart.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <svg className="w-14 h-14 text-surface-border mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              <h3 className="text-base font-heading font-semibold text-text-secondary mb-1">Your cart is empty</h3>
              <p className="text-text-tertiary text-sm mb-5">Add some tickets to get started</p>
              <button onClick={handleContinueShopping} className="btn-primary px-6 py-2.5 text-sm">
                Browse Events
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {(() => {
                // Group table seats (sectionType TABLE) by sectionKey into a single card
                // Non-table items render individually
                const tableGroups = new Map<string, typeof cart.items>()
                const individualItems: typeof cart.items = []

                for (const item of cart.items) {
                  if (item.seatId && item.seatData?.sectionType === 'TABLE' && item.seatData.sectionKey) {
                    const key = `${item.eventId}::${item.seatData.sectionKey}`
                    const group = tableGroups.get(key) || []
                    group.push(item)
                    tableGroups.set(key, group)
                  } else {
                    individualItems.push(item)
                  }
                }

                return (
                  <>
                    {/* Table groups — show as single unit */}
                    {Array.from(tableGroups.entries()).map(([groupKey, seats]) => {
                      const first = seats[0]
                      const totalPrice = seats.reduce((sum, s) => sum + s.price, 0)
                      return (
                        <div key={groupKey} className="border border-surface-border rounded-xl p-4">
                          <h3 className="font-semibold text-text-primary text-sm mb-1 line-clamp-1">
                            {first.eventTitle}
                          </h3>
                          {(first.selectedDate || first.eventStart) && (
                            <div className="text-xs text-text-tertiary mb-1">
                              {first.selectedDate
                                ? formatDate(first.selectedDate, 'MMM dd, yyyy')
                                : formatTimestamp(first.eventStart, 'MMM dd, yyyy')
                              }
                            </div>
                          )}
                          <div className="text-xs font-medium text-primary mb-1">
                            {first.ticketTypeName}
                          </div>
                          <div className="text-xs text-text-secondary mb-1 font-medium">
                            {first.seatData?.sectionName}
                          </div>
                          <div className="text-xs text-text-tertiary mb-2">
                            {seats.length} seat{seats.length !== 1 ? 's' : ''} &middot; {formatPrice(first.allInPrice || first.price)} ea
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <button
                              onClick={() => handleRemoveTableGroup(seats)}
                              className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-error hover:text-error hover:bg-error/5 active:bg-error/10 transition-colors rounded-lg"
                              aria-label={`Remove ${first.seatData?.sectionName}`}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                            <div className="text-right">
                              <div className="text-sm font-heading font-bold text-text-primary">
                                {formatPrice(totalPrice)}
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}

                    {/* Individual items (non-table seats, GA, regular tickets) */}
                    {individualItems.map(item => (
                      <div key={item.id} className="border border-surface-border rounded-xl p-4">
                        <h3 className="font-semibold text-text-primary text-sm mb-1 line-clamp-1">
                          {item.eventTitle}
                        </h3>

                        {item.seasonId ? (
                          <div className="text-xs text-text-tertiary mb-1">
                            {item.seasonName || 'Season Pass'}
                          </div>
                        ) : (item.selectedDate || item.eventStart) ? (
                          <div className="text-xs text-text-tertiary mb-1">
                            {item.selectedDate
                              ? formatDate(item.selectedDate, 'MMM dd, yyyy')
                              : formatTimestamp(item.eventStart, 'MMM dd, yyyy')
                            }
                            {item.waveTime && <span> &bull; {formatTimestamp(item.waveTime, 'h:mm a')}</span>}
                          </div>
                        ) : null}

                        <div className="text-xs font-medium text-primary mb-1">
                          {item.ticketTypeName}
                        </div>

                        {item.seatData?.sectionName && (
                          <div className="text-xs text-text-secondary mb-1 font-medium">
                            {item.seatData.sectionName}
                          </div>
                        )}
                        {item.seatData && item.seatData.sectionType !== 'GA' && item.seatData.sectionType !== 'BOOTH' && item.seatData.sectionType !== 'TABLE' && (
                          <div className="text-xs text-text-secondary mb-2 flex flex-wrap gap-x-2 gap-y-0.5">
                            <span>Row <strong className="text-text-primary">{item.seatData.rowLabel}</strong></span>
                            <span>Seat <strong className="text-text-primary">{item.seatData.seatNumber}</strong></span>
                          </div>
                        )}

                        <div className="flex items-center justify-between mt-2">
                          {item.seatId ? (
                            <button
                              onClick={() => handleRemoveItem(item.id)}
                              className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-error hover:text-error hover:bg-error/5 active:bg-error/10 transition-colors rounded-lg"
                              aria-label="Remove seat"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          ) : (
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                                className="w-11 h-11 rounded-lg border border-surface-border flex items-center justify-center text-text-tertiary hover:border-primary hover:text-primary transition-colors"
                                aria-label="Decrease quantity"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" />
                                </svg>
                              </button>
                              <span className="w-10 text-center text-base font-semibold text-text-primary tabular-nums">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                                disabled={item.quantity >= 10}
                                className="w-11 h-11 rounded-lg border border-surface-border flex items-center justify-center text-text-tertiary hover:border-primary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                aria-label="Increase quantity"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleRemoveItem(item.id)}
                                className="ml-1 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-error hover:text-error hover:bg-error/5 active:bg-error/10 transition-colors rounded-lg"
                                aria-label="Remove item"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          )}
                          <div className="text-right">
                            {!item.seatId && <div className="text-xs text-text-tertiary">{formatPrice(item.price)} ea.</div>}
                            <div className="text-sm font-heading font-bold text-text-primary">
                              {formatPrice(item.price * item.quantity)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )
              })()}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart.items.length > 0 && (
          <div className="border-t border-surface-border p-4 bg-surface-secondary">
            <div className="space-y-1.5 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-text-secondary text-sm">Subtotal</span>
                <span className="text-sm font-medium text-text-primary">{formatPrice(subtotal)}</span>
              </div>
              {feeBreakdown && feeBreakdown.length > 0 ? (
                feeBreakdown.map((fee: { name: string; amount: number }, idx: number) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-text-secondary text-sm">{fee.name}</span>
                    <span className="text-sm font-medium text-text-primary">{formatPrice(fee.amount)}</span>
                  </div>
                ))
              ) : fees > 0 ? (
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary text-sm">Service Fee</span>
                  <span className="text-sm font-medium text-text-primary">{formatPrice(fees)}</span>
                </div>
              ) : null}
              {tax > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary text-sm">Tax</span>
                  <span className="text-sm font-medium text-text-primary">{formatPrice(tax)}</span>
                </div>
              )}
              {discount > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary text-sm">Discount</span>
                  <span className="text-sm font-medium text-success">-{formatPrice(discount)}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-1.5 border-t border-surface-border">
                <span className="text-text-primary font-semibold">Total</span>
                <span className="text-xl font-heading font-bold text-text-primary">{formatPrice(total)}</span>
              </div>
            </div>
            <div className="space-y-2">
              <button onClick={handleCheckout} className="w-full btn-primary py-3">
                Proceed to Checkout
              </button>
              <button onClick={handleContinueShopping} className="w-full btn-secondary py-3">
                Continue Shopping
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CartDrawer
